import { HomeIcon, RefreshCwIcon, TriangleAlertIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isRouteErrorResponse, Link, useRouteError } from 'react-router';

import { EmptyState } from '@/shared/components/empty-state';
import { Button } from '@/shared/ui/button';

import { NotFoundPage } from './NotFoundPage';

/** A lazily loaded chunk disappeared after a deployment: the fix is a reload. */
function isChunkLoadError(error: unknown): boolean {
  return (
    error instanceof Error &&
    /dynamically imported module|Importing a module script failed|error loading dynamically imported module/iu.test(
      error.message,
    )
  );
}

/** Error boundary content for routes (rendered inside the shell, or full-screen at the root). */
export function RouteErrorPage({ fullScreen = false }: { fullScreen?: boolean }) {
  const { t } = useTranslation('app');
  const error = useRouteError();

  if (isRouteErrorResponse(error) && error.status === 404) return <NotFoundPage />;

  const chunk = isChunkLoadError(error);
  const details = import.meta.env.DEV && error instanceof Error ? (error.stack ?? error.message) : null;

  const content = (
    <EmptyState
      size="lg"
      icon={chunk ? RefreshCwIcon : TriangleAlertIcon}
      title={chunk ? t('errors.newVersionTitle') : t('errors.unexpectedTitle')}
      description={chunk ? t('errors.newVersionDescription') : t('errors.unexpectedDescription')}
      action={
        <>
          <Button onClick={() => window.location.reload()}>
            <RefreshCwIcon />
            {t('errors.reload')}
          </Button>
          {!chunk && (
            <Button asChild variant="outline">
              <Link to="/" reloadDocument={fullScreen}>
                <HomeIcon />
                {t('errors.goHome')}
              </Link>
            </Button>
          )}
        </>
      }
    />
  );

  return (
    <div
      className={
        fullScreen
          ? 'flex min-h-dvh items-center justify-center bg-surface p-6'
          : 'flex flex-1 flex-col items-center justify-center'
      }
    >
      {content}
      {details && (
        <pre className="mt-2 max-h-64 w-full max-w-3xl overflow-auto rounded-lg border bg-muted/50 p-4 text-left font-mono text-xs whitespace-pre-wrap text-muted-foreground">
          {details}
        </pre>
      )}
    </div>
  );
}

export function RootErrorPage() {
  return <RouteErrorPage fullScreen />;
}
